// Manager-side Return-to-Work plan panel — Slice 18, DESIGN §6.5 / §9.4 #3.
//
// Four mutually-exclusive states this component renders, in order:
//
//   1. **No `returnToWorkDate` on the case** — render the date setter. This
//      gates the plan: without a confirmed return date, the Pre_Return /
//      Week_1_Back touch points can't be scheduled and the §9.4 #3 pre-return
//      automation has nothing to anchor.
//   2. **Date set, no plan yet** — render the empty plan form. Manager fills
//      workload + phased return + support preferences + (optional) target
//      full-load date + (optional, manager-only) private notes.
//   3. **Plan submitted, not acknowledged** — render the form pre-filled
//      with current values, editable, plus an "awaiting employee
//      acknowledgement" note. Manager can update; server replaces row in
//      place. Once the Employee acks, this view becomes read-only.
//   4. **Plan acknowledged** — read-only summary. Per the Slice 18 design
//      decision (pristine immutability after ack), editing the acked plan
//      requires changing the RTW date — which invalidates the plan and
//      flows back to state 2.
//
// The "change date" affordance is always visible (even in state 4) but
// confirms via a native `confirm()` because it triggers plan invalidation
// per the date-change invalidation rule.

import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PHASED_RETURN_VALUES,
  type CaseSummary,
  type PhasedReturn,
  type ReturnToWorkPlan,
  type RtwRecalculateChoice,
} from '@besideu/shared/cases';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Card } from '@/components/ui/Card.tsx';

const PHASED_LABELS: Record<PhasedReturn, string> = {
  Full_Time: 'Full time from day one',
  Part_Time: 'Reduced hours',
  Staggered: 'Staggered ramp',
};

export function ManagerRtwPlan({
  case: caseRow,
  rtwPlan,
}: {
  case: CaseSummary;
  rtwPlan: ReturnToWorkPlan | null;
}) {
  const queryClient = useQueryClient();
  const invalidateCase = () => queryClient.invalidateQueries({ queryKey: ['cases', caseRow.id] });

  const setDate = useMutation({
    mutationFn: (vars: { returnToWorkDate: string | null; recalculate?: RtwRecalculateChoice }) =>
      api.post<{ case: CaseSummary }>(`/api/cases/${caseRow.id}/return-to-work-date`, {
        returnToWorkDate: vars.returnToWorkDate,
        ...(vars.recalculate ? { recalculate: vars.recalculate } : {}),
      }),
    onSuccess: invalidateCase,
  });
  // mutateAsync so the child can await success before resetting its local
  // draft state — without that, an eager `setDraft('')` on the clear path
  // would empty the input even when the server rejected the mutation.
  const submitDate = (returnToWorkDate: string | null, recalculate?: RtwRecalculateChoice) =>
    setDate.mutateAsync({ returnToWorkDate, ...(recalculate ? { recalculate } : {}) });

  const submitPlan = useMutation({
    mutationFn: (body: {
      workloadExpectations: string;
      phasedReturn: PhasedReturn;
      targetFullLoadDate: string | null;
      supportPreferences: string;
      managerPrivateNotes: string | null;
    }) => api.post<{ rtwPlan: ReturnToWorkPlan }>(`/api/cases/${caseRow.id}/rtw-plan`, body),
    onSuccess: invalidateCase,
  });

  // Compute `acked` as a narrowed rtwPlan (vs a boolean) so the consumer
  // JSX expression below doesn't need a `!` non-null assertion to satisfy
  // the type checker. `acked === null` ↔ no plan or not yet acked.
  const acked = rtwPlan?.acknowledgedAt != null ? rtwPlan : null;

  return (
    <Card as="section" className="space-y-4">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold">Return-to-work plan</h2>
        <p className="text-sm text-muted-foreground">
          Set the return date, then share workload expectations and support preferences. The
          employee acknowledges in their portal before pre-return prompts fire.
        </p>
      </header>

      <RtwDateField
        caseRow={caseRow}
        hasPlan={rtwPlan !== null}
        onSubmit={submitDate}
        pending={setDate.isPending}
        error={errorMessage(setDate.error)}
      />

      {caseRow.returnToWorkDate &&
        (acked ? (
          <AckedSummary plan={acked} />
        ) : (
          <RtwPlanForm
            // Key includes the RTW date so a date change remounts the form,
            // discarding stale `useState` field values AND the `suggestDraft`
            // mutation cache (Maeve suggestions were computed against the old
            // ramp anchor; carrying them forward would mislead the Manager).
            // Also includes `caseRow.id` — belt-and-braces alongside the
            // parent `ManagerRtwPlan`'s own `key={caseId}` (ManagerCaseDetail.tsx):
            // date-only keying would coincidentally NOT remount across two
            // different cases that happen to share the same return date
            // (plausible — a cohort on a fixed-duration leave policy), which
            // would carry case A's workload/support/private-notes draft into
            // case B's form if this component is ever reused somewhere the
            // parent's own key doesn't cover.
            key={`${caseRow.id}:${caseRow.returnToWorkDate}`}
            caseId={caseRow.id}
            initial={rtwPlan}
            onSubmit={(body) => submitPlan.mutate(body)}
            pending={submitPlan.isPending}
            error={errorMessage(submitPlan.error)}
          />
        ))}
    </Card>
  );
}

function RtwDateField({
  caseRow,
  hasPlan,
  onSubmit,
  pending,
  error,
}: {
  caseRow: CaseSummary;
  hasPlan: boolean;
  onSubmit: (date: string | null, recalculate?: RtwRecalculateChoice) => Promise<unknown>;
  pending: boolean;
  error: string | null;
}) {
  const [draft, setDraft] = useState(caseRow.returnToWorkDate ?? '');
  // §17 — when changing an EXISTING date, hold the submit until the manager
  // picks how to handle the already-scheduled support (check-in) dates.
  const [awaitingChoice, setAwaitingChoice] = useState(false);
  const hasDate = caseRow.returnToWorkDate !== null;

  function handle(e: FormEvent) {
    e.preventDefault();
    if (draft === '' || draft === caseRow.returnToWorkDate) return;
    if (hasDate) {
      // Changing an existing date → ask the §17 recalculation question first.
      setAwaitingChoice(true);
      return;
    }
    void onSubmit(draft);
  }

  async function submitWithChoice(choice: RtwRecalculateChoice) {
    setAwaitingChoice(false);
    await onSubmit(draft, choice);
  }

  async function handleClear() {
    const message = hasPlan
      ? 'Clearing the return-to-work date will also remove the current plan and the related touch points. Continue?'
      : 'Clear the return-to-work date?';
    if (!window.confirm(message)) return;
    try {
      // Await success before clearing the local draft. If the mutation
      // rejects, draft stays at its previous value so the input doesn't
      // sit empty while the server still thinks the date is set — the
      // error banner below explains what happened.
      await onSubmit(null);
      setDraft('');
    } catch {
      // Error is surfaced via the `error` prop; nothing to do here.
    }
  }

  return (
    <div className="space-y-3">
      {/* §17 missing-date messaging. */}
      {!hasDate ? (
        <p
          className="rounded-md border border-traffic-amber/40 bg-traffic-amber/10 px-4 py-3 text-sm text-foreground"
          role="status"
        >
          Return date not confirmed. Future touch point dates will be calculated once this is added
          — please confirm it before the return-to-work stage.
        </p>
      ) : null}

      <form onSubmit={handle} className="flex flex-wrap items-end gap-3">
        <label className="min-w-[12rem] flex-1">
          <span className="mb-1 block text-sm font-medium">Return-to-work date</span>
          <input
            type="date"
            className="block w-full rounded-md border border-border bg-background px-3 py-2 text-base"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            required
          />
        </label>
        <Button
          type="submit"
          loading={pending && !awaitingChoice}
          disabled={draft === '' || draft === caseRow.returnToWorkDate}
          variant={hasDate ? 'ghost' : 'primary'}
        >
          {hasDate ? 'Update date' : 'Set date'}
        </Button>
        {hasDate ? (
          <Button
            type="button"
            variant="ghost"
            onClick={handleClear}
            disabled={pending}
            aria-label="Clear return-to-work date"
            className="text-traffic-red hover:text-traffic-red"
          >
            Clear date
          </Button>
        ) : null}
        {error ? (
          <p className="basis-full text-sm text-traffic-red" role="alert">
            {error}
          </p>
        ) : null}
      </form>

      {/* §17 date-change recalculation choice. */}
      {awaitingChoice ? (
        <div className="space-y-2 rounded-md border border-border bg-muted/30 p-4">
          <p className="text-sm font-medium">
            How should the 1, 3, 6 and 9-month support dates be handled?
          </p>
          <p className="text-xs text-muted-foreground">
            Changing the return date also clears the current plan and pre-return touch points — the
            employee re-acknowledges the new plan.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => submitWithChoice('recalculate')} loading={pending}>
              Recalculate dates
            </Button>
            <Button variant="ghost" disabled={pending} onClick={() => submitWithChoice('keep')}>
              Keep current dates
            </Button>
            <Button variant="ghost" disabled={pending} onClick={() => submitWithChoice('ask_hr')}>
              Ask HR to review
            </Button>
            <button
              type="button"
              className="text-xs underline-offset-4 hover:underline disabled:opacity-50"
              disabled={pending}
              onClick={() => setAwaitingChoice(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface RtwPlanDraftSuggestions {
  workloadExpectations: string;
  phasedReturn: PhasedReturn;
  targetFullLoadDate?: string;
  supportPreferences: string;
}

interface RtwPlanDraftResponse {
  suggestions: RtwPlanDraftSuggestions;
  rationale: {
    workloadExpectations: string;
    phasedReturn: string;
    targetFullLoadDate?: string;
    supportPreferences: string;
  };
  message: string | null;
  promptVersionId: string;
}

function RtwPlanForm({
  caseId,
  initial,
  onSubmit,
  pending,
  error,
}: {
  caseId: string;
  initial: ReturnToWorkPlan | null;
  onSubmit: (body: {
    workloadExpectations: string;
    phasedReturn: PhasedReturn;
    targetFullLoadDate: string | null;
    supportPreferences: string;
    managerPrivateNotes: string | null;
  }) => void;
  pending: boolean;
  error: string | null;
}) {
  const [workload, setWorkload] = useState(initial?.workloadExpectations ?? '');
  const [phased, setPhased] = useState<PhasedReturn>(initial?.phasedReturn ?? 'Full_Time');
  const [targetDate, setTargetDate] = useState(initial?.targetFullLoadDate ?? '');
  const [support, setSupport] = useState(initial?.supportPreferences ?? '');
  const [privateNotes, setPrivateNotes] = useState(initial?.managerPrivateNotes ?? '');

  // Full_Time returns don't have a ramp, so the target date input only
  // appears for Part_Time / Staggered. Auto-clear the draft target when
  // switching to Full_Time so a stale value doesn't get posted.
  const showTargetDate = phased !== 'Full_Time';

  // Maeve Sub-mode D fill-helper — DESIGN §6.5. Manager-pulled, one-shot
  // structured suggestions. The Manager opts in field-by-field via Use
  // buttons; we never auto-overwrite their existing form values.
  const suggestDraft = useMutation({
    mutationFn: () =>
      api.post<RtwPlanDraftResponse>(`/api/cases/${caseId}/maeve/rtw-plan-draft`, {
        messages: [{ role: 'user', text: 'Suggest a draft.' }],
      }),
  });

  function applyAllSuggestions(s: RtwPlanDraftSuggestions) {
    setWorkload(s.workloadExpectations);
    setPhased(s.phasedReturn);
    setSupport(s.supportPreferences);
    // Only carry the target date when phased actually needs one — drops a
    // stale Full_Time + date combination that the model might emit.
    if (s.phasedReturn !== 'Full_Time' && s.targetFullLoadDate) {
      setTargetDate(s.targetFullLoadDate);
    } else if (s.phasedReturn === 'Full_Time') {
      setTargetDate('');
    }
  }

  function handle(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      workloadExpectations: workload.trim(),
      phasedReturn: phased,
      targetFullLoadDate: showTargetDate && targetDate !== '' ? targetDate : null,
      supportPreferences: support.trim(),
      managerPrivateNotes: privateNotes.trim() === '' ? null : privateNotes.trim(),
    });
  }

  return (
    <form onSubmit={handle} className="space-y-4">
      {initial ? (
        <p className="rounded-md border border-traffic-amber/40 bg-traffic-amber/10 px-3 py-2 text-sm text-foreground">
          Plan submitted{' '}
          <time dateTime={initial.submittedAt}>
            {new Date(initial.submittedAt).toLocaleString('en-GB')}
          </time>
          . Awaiting employee acknowledgement — you can keep editing until they acknowledge.
        </p>
      ) : null}

      <MaeveSuggestionsPanel
        data={suggestDraft.data ?? null}
        loading={suggestDraft.isPending}
        errorText={errorMessage(suggestDraft.error)}
        onRequest={() => suggestDraft.mutate()}
        onApplyAll={applyAllSuggestions}
        onApplyWorkload={(v) => setWorkload(v)}
        onApplyPhased={(v) => {
          setPhased(v);
          if (v === 'Full_Time') setTargetDate('');
        }}
        onApplyTargetDate={(v) => setTargetDate(v)}
        onApplySupport={(v) => setSupport(v)}
      />

      <label className="block">
        <span className="mb-1 block text-sm font-medium">Workload expectations</span>
        <textarea
          className="block w-full rounded-md border border-border bg-background px-3 py-2 text-base"
          rows={3}
          value={workload}
          onChange={(e) => setWorkload(e.target.value)}
          required
          maxLength={5_000}
          placeholder="What does the employee's day-one workload look like?"
        />
      </label>

      <fieldset>
        <legend className="mb-2 block text-sm font-medium">Phased return</legend>
        <div className="flex flex-wrap gap-3">
          {PHASED_RETURN_VALUES.map((p) => (
            <label key={p} className="inline-flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="phased"
                value={p}
                checked={phased === p}
                onChange={() => setPhased(p)}
              />
              {PHASED_LABELS[p]}
            </label>
          ))}
        </div>
      </fieldset>

      {showTargetDate ? (
        <label className="block">
          <span className="mb-1 block text-sm font-medium">
            Target full-load date{' '}
            <span className="font-normal text-muted-foreground">(optional)</span>
          </span>
          <input
            type="date"
            className="block w-full rounded-md border border-border bg-background px-3 py-2 text-base"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
          />
        </label>
      ) : null}

      <label className="block">
        <span className="mb-1 block text-sm font-medium">Support preferences</span>
        <textarea
          className="block w-full rounded-md border border-border bg-background px-3 py-2 text-base"
          rows={3}
          value={support}
          onChange={(e) => setSupport(e.target.value)}
          required
          maxLength={5_000}
          placeholder="What kind of touch points, accommodations, or quiet time does the employee want?"
        />
      </label>

      <label className="block">
        <span className="mb-1 block text-sm font-medium">
          Private manager notes{' '}
          <span className="font-normal text-muted-foreground">
            (visible to you and HR; not shown to the employee)
          </span>
        </span>
        <textarea
          className="block w-full rounded-md border border-border bg-background px-3 py-2 text-base"
          rows={2}
          value={privateNotes}
          onChange={(e) => setPrivateNotes(e.target.value)}
          maxLength={5_000}
        />
      </label>

      <div className="flex flex-col gap-2">
        <Button type="submit" loading={pending}>
          {initial ? 'Update plan' : 'Submit plan'}
        </Button>
        {error ? (
          <p className="text-sm text-traffic-red" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </form>
  );
}

// Manager Sub-mode D — the Maeve fill-helper panel. Renders the
// "Suggest a draft from Maeve" trigger and, on success, the structured
// per-field suggestions with rationale + Use-this buttons. Designed to
// be opt-in field-by-field so the Manager always has final say (DESIGN
// §6.5 — "fill-helper, not a content author").
function MaeveSuggestionsPanel({
  data,
  loading,
  errorText,
  onRequest,
  onApplyAll,
  onApplyWorkload,
  onApplyPhased,
  onApplyTargetDate,
  onApplySupport,
}: {
  data: RtwPlanDraftResponse | null;
  loading: boolean;
  errorText: string | null;
  onRequest: () => void;
  onApplyAll: (s: RtwPlanDraftSuggestions) => void;
  onApplyWorkload: (v: string) => void;
  onApplyPhased: (v: PhasedReturn) => void;
  onApplyTargetDate: (v: string) => void;
  onApplySupport: (v: string) => void;
}) {
  return (
    <div className="space-y-3 rounded-md border border-border bg-muted/30 p-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">Maeve suggestions</h3>
          <p className="text-xs text-muted-foreground">
            Optional — Maeve drafts each field based on the support pathway context and your
            policies. You decide what to use.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          onClick={onRequest}
          loading={loading}
          disabled={loading}
        >
          {data ? 'Re-suggest' : 'Suggest a draft from Maeve'}
        </Button>
      </header>

      {errorText ? (
        <p className="text-sm text-traffic-red" role="alert">
          {errorText}
        </p>
      ) : null}

      {data ? (
        <div className="space-y-3">
          {data.message ? (
            <p className="text-sm text-muted-foreground italic">{data.message}</p>
          ) : null}

          <SuggestionCard
            label="Workload expectations"
            value={data.suggestions.workloadExpectations}
            rationale={data.rationale.workloadExpectations}
            onUse={() => onApplyWorkload(data.suggestions.workloadExpectations)}
          />
          <SuggestionCard
            label="Phased return"
            value={PHASED_LABELS[data.suggestions.phasedReturn]}
            rationale={data.rationale.phasedReturn}
            onUse={() => onApplyPhased(data.suggestions.phasedReturn)}
          />
          {data.suggestions.phasedReturn !== 'Full_Time' && data.suggestions.targetFullLoadDate ? (
            <SuggestionCard
              label="Target full-load date"
              value={data.suggestions.targetFullLoadDate}
              rationale={data.rationale.targetFullLoadDate ?? ''}
              onUse={() => onApplyTargetDate(data.suggestions.targetFullLoadDate!)}
            />
          ) : null}
          <SuggestionCard
            label="Support preferences"
            value={data.suggestions.supportPreferences}
            rationale={data.rationale.supportPreferences}
            onUse={() => onApplySupport(data.suggestions.supportPreferences)}
          />

          <Button type="button" variant="ghost" onClick={() => onApplyAll(data.suggestions)}>
            Use all suggestions
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function SuggestionCard({
  label,
  value,
  rationale,
  onUse,
}: {
  label: string;
  value: string;
  rationale: string;
  onUse: () => void;
}) {
  return (
    <div className="space-y-2 rounded border border-border bg-background p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="text-xs tracking-wide text-muted-foreground uppercase">{label}</p>
          <p className="text-sm whitespace-pre-wrap">{value}</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          onClick={onUse}
          aria-label={`Use Maeve's ${label} suggestion`}
        >
          Use this
        </Button>
      </div>
      {rationale ? (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">Why:</span> {rationale}
        </p>
      ) : null}
    </div>
  );
}

function AckedSummary({ plan }: { plan: ReturnToWorkPlan }) {
  const ackedAt = useMemo(
    () => (plan.acknowledgedAt ? new Date(plan.acknowledgedAt).toLocaleString('en-GB') : null),
    [plan.acknowledgedAt],
  );
  return (
    <div className="space-y-3 rounded-md border border-traffic-green/30 bg-traffic-green/10 p-4 text-sm">
      <p className="font-medium text-traffic-green-text">
        Plan acknowledged by the employee on <time dateTime={plan.acknowledgedAt!}>{ackedAt}</time>.
      </p>
      <PlanReadOnly plan={plan} />
      <p className="text-xs text-muted-foreground">
        To change the plan, update the return-to-work date above — that clears the current plan so
        you can submit a new one.
      </p>
    </div>
  );
}

function PlanReadOnly({ plan }: { plan: ReturnToWorkPlan }) {
  return (
    <dl className="space-y-3">
      <Field label="Workload expectations">{plan.workloadExpectations}</Field>
      <Field label="Phased return">
        {PHASED_LABELS[plan.phasedReturn]}
        {plan.targetFullLoadDate ? ` — target full load by ${plan.targetFullLoadDate}` : ''}
      </Field>
      <Field label="Support preferences">{plan.supportPreferences}</Field>
      {plan.managerPrivateNotes ? (
        <Field label="Private manager notes (not shown to employee)">
          {plan.managerPrivateNotes}
        </Field>
      ) : null}
    </dl>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs tracking-wide text-muted-foreground uppercase">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap">{children}</dd>
    </div>
  );
}

function errorMessage(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    // 503 is shared between the submit-plan path (never produces it) and
    // the Maeve fill-helper (DESIGN §6.5) — the suggest-draft route 503s
    // when no CGC-approved `Manager_Guidance` prompt is active. Surfacing
    // the operator-actionable copy here so the Manager knows it's a
    // platform-side configuration issue, not their fields.
    if (err.status === 503)
      return "Maeve isn't available right now — a besideU administrator needs to enable the Manager prompt.";
    if (err.status === 502)
      return "Maeve couldn't generate a draft just now. Please try again in a moment.";
    if (err.status === 409) {
      const code =
        (err.body && typeof err.body === 'object' && 'error' in err.body
          ? String((err.body as { error?: unknown }).error)
          : '') ?? '';
      if (code === 'rtw_date_required') return 'Set the return-to-work date first.';
      if (code === 'locked_after_ack')
        return 'The employee has already acknowledged this plan. Change the return-to-work date to submit a new one.';
      if (code === 'concurrent_submission')
        return 'Someone else just submitted this plan. Refresh to see the current version.';
    }
    if (err.status === 400) return 'Could not save — check the fields and try again.';
    if (err.status === 404) return 'Support pathway not found.';
  }
  return 'Could not save. Please try again.';
}
